/**
 * ProfilerContext
 *
 * React context for managing profiler state and exposing profiling data to UI components.
 * Integrates with ProfilerService to receive profiling sessions and maintain history.
 */

import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {ProfilerContextValue, ProfileSession} from '../@types/profiler';
import {profilerService} from '../services/profiler.service';

const MAX_HISTORY_SESSIONS = 20;

// Create context with empty object cast (will be replaced by provider)
const ProfilerContext = createContext<ProfilerContextValue>({} as ProfilerContextValue);

interface ProfilerProviderProps {
    children: ReactNode;
}

export const ProfilerProvider: React.FC<ProfilerProviderProps> = ({ children }) => {
    // State
    const [isEnabled, setIsEnabled] = useState<boolean>(profilerService.getIsEnabled());
    const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
    const [currentSession, setCurrentSession] = useState<ProfileSession | null>(null);
    const [history, setHistory] = useState<ProfileSession[]>(() => {
        // Load history from localStorage
        const saved = localStorage.getItem('profilerHistory');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Restore Date objects
                return parsed.map((session: ProfileSession) => ({
                    ...session,
                    timestamp: new Date(session.timestamp)
                }));
            } catch {
                return [];
            }
        }
        return [];
    });
    const [activeEsdName, setActiveEsdName] = useState<string | null>(null);

    // Subscribe to profiler service for new sessions
    useEffect(() => {
        const unsubscribe = profilerService.subscribe((session: ProfileSession) => {
            setCurrentSession(session);
            setHistory(prev => {
                const updated = [session, ...prev].slice(0, MAX_HISTORY_SESSIONS);
                // Persist to localStorage
                localStorage.setItem('profilerHistory', JSON.stringify(updated));
                return updated;
            });
            // Panel only opens on-demand via "Show Profiler" menu item
        });

        return unsubscribe;
    }, []);

    // Actions
    const enable = useCallback((): void => {
        profilerService.enable();
        setIsEnabled(true);
    }, []);

    const disable = useCallback((): void => {
        profilerService.disable();
        setIsEnabled(false);
    }, []);

    const toggle = useCallback((): void => {
        profilerService.toggle();
        setIsEnabled(profilerService.getIsEnabled());
    }, []);

    const openPanel = useCallback((): void => {
        setIsPanelOpen(true);
    }, []);

    const closePanel = useCallback((): void => {
        setIsPanelOpen(false);
    }, []);

    const togglePanel = useCallback((): void => {
        setIsPanelOpen(prev => !prev);
    }, []);

    const clearHistory = useCallback((): void => {
        setHistory([]);
        setCurrentSession(null);
        localStorage.removeItem('profilerHistory');
    }, []);

    const recordSession = useCallback((session: ProfileSession): void => {
        setCurrentSession(session);
        setHistory(prev => {
            const updated = [session, ...prev].slice(0, MAX_HISTORY_SESSIONS);
            localStorage.setItem('profilerHistory', JSON.stringify(updated));
            return updated;
        });
    }, []);

    const contextValue: ProfilerContextValue = {
        // State
        isEnabled,
        isPanelOpen,
        currentSession,
        history,
        activeEsdName,
        // Actions
        enable,
        disable,
        toggle,
        openPanel,
        closePanel,
        togglePanel,
        clearHistory,
        recordSession,
        setActiveEsdName
    };

    return (
        <ProfilerContext.Provider value={contextValue}>
            {children}
        </ProfilerContext.Provider>
    );
};

/**
 * Hook to access profiler context
 */
export const useProfiler = (): ProfilerContextValue => {
    const context = useContext(ProfilerContext);
    // Return context even if potentially empty - let components handle gracefully
    return context;
};

export default ProfilerContext;
